import Business from '@mui/icons-material/Business';
import { Box, Grid, List, Paper } from '@mui/material';
import {
  IDepartmentViewModel,
  ThirdPartEnum,
} from 'Interfaces/ICompaniesConfig';
import { ICompanyProductViewModel } from 'Interfaces/ICompanyProductConfig';
import { IEmployeeViewModel } from 'Interfaces/IEmployeeConfig';
import { IAddressViewModel } from 'Interfaces/IGlobalTypeConfig';
import AlphabetSortDrawer from 'components/ui/AlphabetSortDrawer';
import { PageHeader } from 'components/ui/Global/PageHeader';
import { SearchFilter } from 'components/ui/SearchFilter';
import { SingleTicketEmployeeView } from 'components/ui/SingleTicketEmployeeView';
import { getFilteredList } from 'functions/misc';
import useWindowsDimension from 'functions/useWindowsDimension';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const initialEmployeeState: IEmployeeViewModel = {
  id: 'new',
  name: '',
  phone: '',
  email: '',
  departmentId: '',
  department: {} as unknown as IDepartmentViewModel,
  address: {} as unknown as IAddressViewModel,
  role: 0,
  primaryContact: false,
  globalAdministrator: false,
  budget: 0,
  title: '',
  companyProducts: [] as unknown as ICompanyProductViewModel[],
  workRole: null,
  url: '',
  legacyId: 0,
  thirdPartyId: '',
  thirdParty: ThirdPartEnum.None,
  responsibleFor: [] as unknown as IEmployeeViewModel[],
};

type EmployeeListProps = {
  handleChange: (
    event: React.MouseEvent<HTMLElement>,
    employee: IEmployeeViewModel
  ) => void;
  employees: IEmployeeViewModel[];
};

export const EmployeeList = ({
  handleChange,
  employees,
}: EmployeeListProps) => {
  const { t } = useTranslation();
  const { height } = useWindowsDimension();

  // State
  const [employee, setEmployee] =
    useState<IEmployeeViewModel>(initialEmployeeState);
  const [filteredEmployees, setFilteredEmployees] = useState<
    IEmployeeViewModel[]
  >([]);
  const [letterFilter, setLetterFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Handlers

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (val: number) => {};

  useEffect(() => {
    let result = employees;

    if (letterFilter) {
      result = result.filter((emp) =>
        emp.name?.toLowerCase().startsWith(letterFilter.toLowerCase())
      );
    }

    if (searchQuery) {
      result = getFilteredList(result, searchQuery);
    }

    setFilteredEmployees(result);
  }, [employees, letterFilter, searchQuery]);

  // Whenever 'company' changes, call the parent's 'handleChange'

  return (
    <Grid container sx={{ display: 'flex', flexDirection: 'row', flexGrow: 1 }}>
      <Grid item xs={12}>
        <PageHeader
          Icon={Business}
          title={t('employee.header')}
          FormJSX={null}
          HeaderButtons={[]}
        />
      </Grid>

      <Grid item xs={12} sx={{ pr: 1 }}>
        <SearchFilter
          Icon={Business}
          Header={t('employee.header')}
          filterValue={null}
          handleFilterChange={handleFilterChange}
          handleSearch={handleSearch}
          handleNew={null}
          selectList={null}
        />

        <Box sx={{ display: 'flex' }}>
          <AlphabetSortDrawer
            buttonIcon={<></>}
            buttonContext='employees'
            handleChange={() => {}}
            onLetterChange={(letter: string) => setLetterFilter(letter)}
          />

          <List
            sx={{
              height: Math.ceil(height - 244),
              width: '100%',
              mb: 0,
              p: 2,
              backgroundColor: 'primary.back',
              overflow: 'auto',
            }}
          >
            {filteredEmployees.length === 0 && <div>No data</div>}
            {filteredEmployees.map((emp, index) => (
              <Grid
                key={index}
                sx={{ cursor: 'pointer', marginBottom: 1 }}
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                  // 1) Set the chosen company in your local state
                  setEmployee(emp);
                  // 2) Call the parent's handleChange
                  handleChange(e, emp);
                }}
              >
                <SingleTicketEmployeeView employee={emp} />
              </Grid>
            ))}
          </List>
        </Box>

        <Paper
          variant='elevation3'
          sx={{
            p: 2,
            height: 64,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        />
      </Grid>
    </Grid>
  );
};
